import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  MailVolumeData,
  CurrentWeekData,
} from "../../api/fetchCampaignStatusMetadata/types";

interface MailVolumeTimelineCardProps {
  mailVolumeData: MailVolumeData;
  currentWeekData: CurrentWeekData;
  mostRecentPostageFileUpdatedAt: string | null;
}

interface WeekCardData {
  weekKey: string;
  weekNumber: number;
  quantity: number;
  cost: string;
  isProjected: boolean;
  isCurrent: boolean;
}

export const MailVolumeTimelineCard: React.FC<MailVolumeTimelineCardProps> = ({
  mailVolumeData,
  currentWeekData,
  mostRecentPostageFileUpdatedAt,
}) => {
  const weekCards: WeekCardData[] = Object.entries(mailVolumeData)
    .map(([weekKey, data]) => {
      const weekNumber = parseInt(weekKey.replace("week ", ""), 10);
      return {
        weekKey,
        weekNumber,
        quantity: data.quantity,
        cost: data.cost,
        isProjected: data.isProjected,
        isCurrent: weekNumber === currentWeekData.weekNumber,
      };
    })
    .sort((a, b) => a.weekNumber - b.weekNumber);

  const getWeekLabel = (weekNumber: number, isCurrent: boolean): string => {
    if (isCurrent) return "CURRENT WEEK";
    const currentWeek = currentWeekData.weekNumber;
    if (weekNumber < currentWeek) return "LAST WEEK";
    return "NEXT WEEK";
  };

  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatQuantity = (value: number): string => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-sm font-semibold tracking-wide">
            MAIL VOLUME TIMELINE
          </CardTitle>
          {mostRecentPostageFileUpdatedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Postage sync date: {mostRecentPostageFileUpdatedAt}
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {weekCards.map((week) => (
            <div
              className={`text-center rounded-lg p-3 ${
                week.isCurrent
                  ? "bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-400 shadow-md"
                  : "bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-300"
              }`}
              key={week.weekKey}
            >
              <p
                className={`text-xs font-medium uppercase mb-2 ${
                  week.isCurrent ? "text-blue-600" : "text-gray-500"
                }`}
              >
                {getWeekLabel(week.weekNumber, week.isCurrent)}
              </p>

              <p
                className={`text-sm mb-1 ${week.isCurrent ? "text-blue-600" : "text-gray-600"}`}
              >
                Week {week.weekNumber}
              </p>

              <div className="mb-2">
                <p
                  className={`text-xl font-bold mb-1 ${week.isCurrent ? "text-blue-900" : "text-gray-900"}`}
                >
                  {formatQuantity(week.quantity)}
                </p>
                <p
                  className={`text-xs ${
                    week.isProjected
                      ? week.isCurrent
                        ? "text-blue-600"
                        : "text-gray-600"
                      : "text-gray-600"
                  }`}
                >
                  {week.isProjected ? "Projected" : "Mailed"}
                </p>
              </div>

              <div
                className={`pt-2 border-t ${week.isCurrent ? "border-blue-300" : "border-gray-300"}`}
              >
                <p
                  className={`text-base font-bold ${week.isCurrent ? "text-blue-800" : "text-gray-800"}`}
                >
                  {formatCurrency(week.cost)}
                </p>
                <p
                  className={`text-xs ${
                    week.isProjected
                      ? week.isCurrent
                        ? "text-blue-600"
                        : "text-gray-600"
                      : "text-gray-600"
                  }`}
                >
                  {week.isProjected ? "Est. postage" : "Postage"}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
